/**
 * Badge — small contextual label. Pill-shaped or square-cornered.
 *
 * <Badge variant="success">In Stock</Badge>
 * <Badge variant="veg" icon /> Veg
 * <Badge variant="food">FOOD</Badge>
 */
import React from 'react';
import { css } from './tokens';

export type BadgeVariant =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'primary'
  | 'accent'
  | 'food'
  | 'rides'
  | 'veg'
  | 'nonveg'
  | 'halal';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  pill?: boolean;
  size?: 'sm' | 'md';
  icon?: React.ReactNode;
}

function getColors(variant: BadgeVariant): { bg: string; fg: string; border?: string } {
  switch (variant) {
    case 'success': return { bg: css.color.successSoft, fg: css.color.primaryStrong };
    case 'warning': return { bg: css.color.warningSoft, fg: '#92400E' };
    case 'danger':  return { bg: css.color.dangerSoft, fg: css.color.danger };
    case 'info':    return { bg: css.color.infoSoft, fg: '#075985' };
    case 'primary': return { bg: css.color.primary, fg: css.color.textOnPrimary };
    case 'accent':  return { bg: css.color.accent, fg: css.color.textOnAccent };
    case 'food':    return { bg: 'rgba(226,55,68,0.10)', fg: css.color.food };
    case 'rides':   return { bg: css.color.secondarySoft, fg: css.color.secondary };
    case 'veg':     return { bg: '#FFFFFF', fg: css.color.veg, border: css.color.veg };
    case 'nonveg':  return { bg: '#FFFFFF', fg: css.color.nonveg, border: css.color.nonveg };
    case 'halal':   return { bg: 'rgba(4,120,87,0.10)', fg: css.color.halal };
    case 'neutral':
    default:        return { bg: css.color.surface2, fg: css.color.text };
  }
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  pill = false,
  size = 'sm',
  icon,
  style,
  className,
  children,
  ...rest
}) => {
  const { bg, fg, border } = getColors(variant);
  const sm = size === 'sm';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: bg,
        color: fg,
        border: border ? `1px solid ${border}` : '1px solid transparent',
        borderRadius: pill ? 999 : 6,
        padding: sm ? '2px 8px' : '4px 10px',
        fontSize: sm ? 11 : 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        ...style,
      }}
      className={['pm-badge', `pm-badge--${variant}`, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
};

/** Compact veg / non-veg dot used on menu items + product cards. */
export const VegDot: React.FC<{ veg: boolean; size?: number; halal?: boolean }> = ({
  veg,
  size = 12,
  halal,
}) => {
  const color = halal ? css.color.halal : veg ? css.color.veg : css.color.nonveg;
  return (
    <span
      aria-label={halal ? 'Halal' : veg ? 'Vegetarian' : 'Non-vegetarian'}
      title={halal ? 'Halal' : veg ? 'Vegetarian' : 'Non-vegetarian'}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        border: `1.5px solid ${color}`,
        borderRadius: 2,
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: size * 0.45,
          height: size * 0.45,
          borderRadius: '50%',
          background: color,
        }}
      />
    </span>
  );
};

export default Badge;

